// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscriptionConfig, TySubscriptionPeriod } from "@/types";
import { formatMoney } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyPlanRequestDetails = {
    config: TySubscriptionConfig;
    /** Which card was pressed on the way in. The choice is still changeable here. */
    period?: TySubscriptionPeriod;
    /** A company that already holds a period is renewing, which is a different sentence. */
    isRenewal?: boolean;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * Asking to pay. It writes a request, nothing more — **billing is manual**, so the period begins
 * when a Lozicore admin confirms the transfer, and this popup is careful never to imply otherwise.
 *
 * The choice and the review are one step rather than two. There are two periods and one addon, so a
 * separate confirmation screen would be a second press to read the same four lines back.
 *
 * Every figure is computed from `config`, which came from the backend. Nothing here knows a price,
 * because a dashboard that remembers one will eventually quote a company a figure we have changed.
 */
export default function PlanRequestPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { config, period: initial, isRenewal, onDone } = details as TyPlanRequestDetails;

    const [period, setPeriod] = useState<TySubscriptionPeriod>(initial ?? config.periods[0]);
    const [addons, setAddons] = useState(0);

    const { loading, error, succeededAt, invoke } = useFetch("/subscription", { method: "POST" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onDone();
        onClose();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const planPrice = config.prices[period];
    const addonPrice = config.storageAddon[period];
    const total = planPrice + addons * addonPrice;
    const storage = config.planStorageGb + addons * config.storageAddon.gb;

    return (
        <section className="side-form side-form--center plan-request">
            <header className="side-form__header">
                <h2 className="side-form__title">{isRenewal ? t.subscription.renewTitle : t.subscription.choosePlan}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="plan-request__lead">{isRenewal ? t.subscription.renewHint : t.subscription.choosePlanHint}</p>

                <div className="plan-request__periods">
                    {config.periods.map((name) => {
                        /**
                         * What the longer period saves against paying the short one four times over.
                         * Derived, never written down: a price change has to move this number with it.
                         */
                        const perMonth = config.prices[name] / config.months[name];
                        const dearest = Math.max(...config.periods.map((item) => config.prices[item] / config.months[item]));
                        const saved = dearest > 0 ? Math.round((1 - perMonth / dearest) * 100) : 0;

                        return (
                            <button
                                key={name}
                                type="button"
                                className={`plan-request__period${period === name ? " plan-request__period--on" : ""}`}
                                onClick={() => setPeriod(name)}
                                disabled={loading}
                                aria-pressed={period === name}
                            >
                                {saved > 0 && (
                                    <span className="plan-request__save">{t.subscription.saveLabel.replace("{percent}", String(saved))}</span>
                                )}
                                <span className="plan-request__period-name">{t.subscription.periods[name]}</span>
                                <span className="plan-request__period-price">
                                    {formatMoney(config.prices[name])}
                                    <small>{t.properties.currency}</small>
                                </span>
                                <span className="plan-request__period-every">{t.subscription.periodEvery[name]}</span>
                            </button>
                        );
                    })}
                </div>

                {/* The one thing a paid plan is limited by, so it is the one thing on sale beside it. */}
                <div className="plan-request__addon">
                    <div className="plan-request__addon-text">
                        <span className="plan-request__addon-title">{t.subscription.extraStorage}</span>
                        <span className="plan-request__addon-hint">
                            {t.subscription.extraStorageHint
                                .replace("{gb}", String(config.storageAddon.gb))
                                .replace("{price}", formatMoney(addonPrice))
                                .replace("{currency}", t.properties.currency)}
                        </span>
                    </div>

                    <div className="plan-request__stepper">
                        <button
                            type="button"
                            className="plan-request__step"
                            onClick={() => setAddons((count) => Math.max(0, count - 1))}
                            disabled={loading || addons <= 0}
                            aria-label="-"
                        >
                            −
                        </button>
                        <span className="plan-request__count">{addons}</span>
                        <button
                            type="button"
                            className="plan-request__step"
                            onClick={() => setAddons((count) => Math.min(config.addonMax, count + 1))}
                            disabled={loading || addons >= config.addonMax}
                            aria-label="+"
                        >
                            +
                        </button>
                    </div>
                </div>

                <div className="plan-request__lines">
                    <div className="plan-request__line">
                        <span>{t.subscription.planLine.replace("{period}", t.subscription.periods[period])}</span>
                        <span>
                            {formatMoney(planPrice)} {t.properties.currency}
                        </span>
                    </div>

                    {addons > 0 && (
                        <div className="plan-request__line">
                            <span>{t.subscription.addonsLine.replace("{count}", String(addons))}</span>
                            <span>
                                {formatMoney(addons * addonPrice)} {t.properties.currency}
                            </span>
                        </div>
                    )}

                    <div className="plan-request__line plan-request__line--quiet">
                        <span>{t.subscription.totalStorage}</span>
                        <span>
                            {storage} {t.subscription.gb}
                        </span>
                    </div>

                    <div className="plan-request__line plan-request__line--total">
                        <span>{t.subscription.total}</span>
                        <span>
                            {formatMoney(total)} {t.properties.currency}
                        </span>
                    </div>
                </div>

                <p className="side-form__hint">{t.subscription.reviewHint}</p>
                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button
                    type="button"
                    className="side-form__submit"
                    onClick={() => invoke({ method: "POST", data: { period, addons } })}
                    disabled={loading}
                >
                    {loading ? t.common.loading : t.subscription.request}
                </button>
            </footer>
        </section>
    );
}
